/**
 * Device Capability Detection for Adaptive Performance Optimization
 *
 * Detects device capabilities to provide appropriate performance strategies
 * without degrading experience for powerful devices.
 */

class DeviceCapabilityDetector {
  constructor() {
    this.deviceInfo = this.detectDeviceCapabilities();
  }

  /**
   * Development-only logging to avoid console spam in production
   */
  devLog(message, ...args) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(message, ...args);
    }
  }

  /**
   * Detect device capabilities based on multiple factors
   */
  detectDeviceCapabilities() {
    const memory = this.getMemoryInfo();
    const performance = this.getPerformanceInfo();
    const hardware = this.getHardwareInfo();

    return {
      memory,
      performance,
      hardware,
      tier: this.calculateDeviceTier(memory, performance, hardware),
    };
  }

  /**
   * Get memory-related information
   */
  getMemoryInfo() {
    const info = {
      totalJS: 0,
      usedJS: 0,
      availableJS: 0,
      isLowMemory: false,
      estimatedDeviceRAM: 'unknown',
    };

    if (performance.memory) {
      info.totalJS = Math.round(
        performance.memory.jsHeapSizeLimit / (1024 * 1024),
      );
      info.usedJS = Math.round(
        performance.memory.usedJSHeapSize / (1024 * 1024),
      );
      info.availableJS = info.totalJS - info.usedJS;

      // Estimate device RAM based on JS heap limit
      if (info.totalJS < 1000) {
        info.estimatedDeviceRAM = '2GB';
        info.isLowMemory = true;
      } else if (info.totalJS < 2000) {
        info.estimatedDeviceRAM = '3-4GB';
      } else if (info.totalJS < 4000) {
        info.estimatedDeviceRAM = '6-8GB';
      } else {
        info.estimatedDeviceRAM = '8GB+';
      }
    }

    return info;
  }

  /**
   * Get performance-related information
   */
  getPerformanceInfo() {
    const info = {
      cores: navigator.hardwareConcurrency || 4,
      connectionSpeed: 'unknown',
      isSlowDevice: false,
    };

    // Check network connection
    if (navigator.connection) {
      info.connectionSpeed = navigator.connection.effectiveType || 'unknown';
    }

    // Simple performance test
    const startTime = performance.now();
    let iterations = 0;
    while (performance.now() - startTime < 10) {
      // 10ms test
      iterations++;
    }

    info.performanceScore = iterations;
    info.isSlowDevice = iterations < 50000; // Rough threshold

    return info;
  }

  /**
   * Get hardware-related information
   */
  getHardwareInfo() {
    const userAgent = navigator.userAgent.toLowerCase();
    const info = {
      isMobile: /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent,
      ),
      isAndroid: /android/i.test(userAgent),
      isIOS: /iphone|ipad|ipod/i.test(userAgent),
      screenSize: `${window.screen.width}x${window.screen.height}`,
      pixelRatio: window.devicePixelRatio || 1,
      isLowEndDevice: false,
    };

    // Detect specific low-end devices
    if (userAgent.includes('redmi') && userAgent.includes('9c')) {
      info.isLowEndDevice = true;
      info.deviceModel = 'Redmi 9C';
    }

    // General low-end indicators
    if (info.isMobile && window.screen.width <= 720 && info.pixelRatio <= 1.5) {
      info.isLowEndDevice = true;
    }

    return info;
  }

  /**
   * Calculate overall device tier
   */
  calculateDeviceTier(memory, performance, hardware) {
    let score = 0;

    // Memory scoring
    if (memory.totalJS > 4000) score += 3;
    else if (memory.totalJS > 2000) score += 2;
    else if (memory.totalJS > 1000) score += 1;

    // Performance scoring
    if (performance.performanceScore > 100000) score += 3;
    else if (performance.performanceScore > 50000) score += 2;
    else score += 1;

    // Hardware scoring
    if (hardware.isLowEndDevice) score -= 2;
    if (hardware.isMobile) score -= 1;
    if (performance.cores >= 8) score += 2;
    else if (performance.cores >= 4) score += 1;

    // Determine tier
    if (score >= 6) return 'HIGH_END';
    if (score >= 3) return 'MID_RANGE';
    return 'LOW_END';
  }

  /**
   * Get memory thresholds based on device capability
   */
  getMemoryThresholds() {
    // eslint-disable-next-line no-unused-vars
    const { tier, memory } = this.deviceInfo;

    switch (tier) {
      case 'HIGH_END':
        return {
          warning: 1000, // 1GB
          cleanup: 1200, // 1.2GB
          emergency: 1500, // 1.5GB
        };
      case 'MID_RANGE':
        return {
          warning: 600, // 600MB
          cleanup: 800, // 800MB
          emergency: 1000, // 1GB
        };
      case 'LOW_END':
      default:
        return {
          warning: 300, // 300MB
          cleanup: 400, // 400MB
          emergency: 500, // 500MB
        };
    }
  }

  /**
   * Get processing limits based on device capability
   */
  getProcessingLimits() {
    const { tier } = this.deviceInfo;

    switch (tier) {
      case 'HIGH_END':
        return {
          maxProducts: 10000,
          chunkSize: 2000,
          maxChunkTime: 50,
          enableWebWorkers: true,
          enableAdvancedFilters: true,
        };
      case 'MID_RANGE':
        return {
          maxProducts: 5000,
          chunkSize: 1000,
          maxChunkTime: 16,
          enableWebWorkers: true,
          enableAdvancedFilters: true,
        };
      case 'LOW_END':
      default:
        return {
          maxProducts: 2000,
          chunkSize: 500,
          maxChunkTime: 8,
          enableWebWorkers: false,
          enableAdvancedFilters: false,
        };
    }
  }

  /**
   * Get feature flags based on device capability
   */
  getFeatureFlags() {
    const { tier, memory } = this.deviceInfo;

    return {
      enableImagePreloading: tier !== 'LOW_END',
      enableAdvancedAnimations: tier === 'HIGH_END',
      enableCaching: memory.totalJS > 1000,
      maxCacheSize: tier === 'HIGH_END' ? 50 : tier === 'MID_RANGE' ? 20 : 10, // MB
      enableProgressiveLoading: tier === 'LOW_END',
      enableMemoryMonitoring: true,
      aggressiveCleanup: tier === 'LOW_END',
    };
  }

  /**
   * Log device information for debugging
   */
  logDeviceInfo() {
    this.devLog(`Device Tier: ${this.deviceInfo.tier}`);
    this.devLog(`Estimated RAM: ${this.deviceInfo.memory.estimatedDeviceRAM}`);
    this.devLog(`JS Heap Limit: ${this.deviceInfo.memory.totalJS}MB`);
    this.devLog(`CPU Cores: ${this.deviceInfo.performance.cores}`);
    this.devLog(
      `Performance Score: ${this.deviceInfo.performance.performanceScore}`,
    );
    this.devLog(`Is Mobile: ${this.deviceInfo.hardware.isMobile}`);
    this.devLog(
      `Is Low-end Device: ${this.deviceInfo.hardware.isLowEndDevice}`,
    );
    this.devLog('Memory Thresholds:', this.getMemoryThresholds());
    this.devLog('Processing Limits:', this.getProcessingLimits());
    this.devLog('Feature Flags:', this.getFeatureFlags());
  }

  /**
   * Get current memory usage percentage based on device capability
   */
  getMemoryUsageInfo() {
    if (!performance.memory) return null;

    const usedMB = Math.round(
      performance.memory.usedJSHeapSize / (1024 * 1024),
    );
    const thresholds = this.getMemoryThresholds();

    return {
      usedMB,
      warningThreshold: thresholds.warning,
      cleanupThreshold: thresholds.cleanup,
      emergencyThreshold: thresholds.emergency,
      isWarning: usedMB > thresholds.warning,
      needsCleanup: usedMB > thresholds.cleanup,
      isEmergency: usedMB > thresholds.emergency,
      usagePercentage: Math.round((usedMB / thresholds.emergency) * 100),
    };
  }
}

// Singleton instance
const deviceCapabilityDetector = new DeviceCapabilityDetector();

export default deviceCapabilityDetector;
